// src/pages/FrogProductDetailPage.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductById as getFrogProductById, checkRestockNotifyAll, subscribeRestockNotify, unsubscribeRestockNotify, getActivePromos, getPromoBySlug, getPublicSpeciesCareGuide } from '../services/api';
import { useAuth } from '../context/AuthContext';
import VariationCard from '../components/Products/VariationCard';
import AnimalListingCard from '../components/Products/AnimalListingCard';
import LotListingCard from '../components/Products/LotListingCard';
import { resolveImageUrls, resolveGalleryImages, buildAssetUrl } from '../utils/imageResolver';
import PhotoCarousel from '../components/common/PhotoCarousel';
import { Container, Row, Col } from 'react-bootstrap';
import SEO from '../components/SEO';
import SkeletonCardGrid from '../components/common/SkeletonCardGrid';
import WeatherTrustBlock from '../components/common/WeatherTrustBlock';
import TrustHighlights from '../components/common/TrustHighlights';
import ArrivalNotifyForm from '../components/common/ArrivalNotifyForm';
import { BookOpen, Tag } from 'lucide-react';
import { createProductSchema, createBreadcrumbSchema, getGenusKeywords } from '../constants/seoKeywords';
import { createSpeciesSlug } from '../utils/slugUtils';
import Breadcrumbs from '../components/Navigation/Breadcrumbs';
import '../styles/products.css';
import '../styles/skeletons.css';
import { scrollIntoViewBelowTopBar } from '../utils/scrollHelpers';
import { formatSpeciesName, formatLocality } from '../utils/scientificName';
import { formatProductName } from '../utils/productName';

/* ── Unified listing grid: sort control (customer slice 2026-08) ──────
   Operator problem: a $125 sexed pair rendered BELOW four $50 stock
   cards because lots/animals lived in a separate "Available Animals"
   section under the stock grid. Premium listings got buried, and the
   heading was wrong for a pair/group/bundle anyway.

   Fix: ONE grid holding everything purchasable on the product (pinned
   pairs, pinned groups, pool bundles, individual animals, stock
   variations) plus a customer-facing sort control. Each card already
   carries its own type badge ("Sexed Pair" / "Group of N" / "One of a
   kind" / the variation label), so the type HEADINGS are unnecessary.

   Everything below is CLIENT-SIDE over the already-loaded detail
   payload — no extra fetches, no query params.  */

const SORT_MODES = [
  { value: 'featured', label: 'Featured' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'recent', label: 'Recently added' },
];
const DEFAULT_SORT_MODE = 'featured';

/* "Featured" type order — operator-stated default:
   Pairs → Individuals → Groups (picked groups AND pool bundles) → Stock.
   EDIT THESE NUMBERS to reorder; nothing else needs to change. Ties
   within a rank break on price DESCENDING (premium first), then on
   payload order (which keeps one variation's animals together). */
const FEATURED_TYPE_RANK = {
  pair: 0,
  animal: 1,
  group: 2,   // picked groups + pool bundles
  stock: 3,
};

const toNumber = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toTimestamp = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/* Price a STOCK variation card the way the card actually DISPLAYS it:
   VariationCard shows the promo price when promo_display is in
   per_unit_price mode, otherwise latest_price. Sorting on anything else
   would put cards in an order the customer can't see. */
const stockCardPrice = (v) => {
  const promo = v?.promo_display;
  if (promo?.display_mode === 'per_unit_price' && promo?.promo_price != null) {
    return toNumber(promo.promo_price);
  }
  return toNumber(v?.latest_price?.price) ?? toNumber(v?.effective_price);
};

const variationLabel = (v) => {
  // S3-01.5: a package's card is the box itself — its own name, not
  // "<anchor component> — Package".
  if (v?.is_package && v?.product_name) return formatProductName(v, { name: v.product_name });
  const type = v?.variation_type?.name || v?.variation_type_name || '';
  return v?.morph_name ? `${type} — ${v.morph_name}` : type;
};

/* Missing prices always sink to the bottom in BOTH price directions —
   "cheapest first" must never be led by an unpriced card. */
const comparePrice = (a, b, dir) => {
  if (a.price == null && b.price == null) return 0;
  if (a.price == null) return 1;
  if (b.price == null) return -1;
  return dir * (a.price - b.price);
};

/* UNIVERSAL primary key, applied before every sort mode: a sold-out /
   not-for-sale card never leads the grid. This preserves the behavior
   the old two-section layout got for free from the fetch-time
   `b.is_for_sale - a.is_for_sale` variation sort — without it, an
   out-of-stock $90 stock card would outrank an in-stock $50 one under
   "Featured" (price DESC) and lead the page under "Price: High to Low".
   Only stock cards can be unavailable; lots and individual animals are
   listed by the API only when purchasable. */
const compareAvailability = (a, b) => (b.available ? 1 : 0) - (a.available ? 1 : 0);

// Module-scope promo-deal memo: {productId: {deal, at}}. Survives
// navigation within the session; 5-minute TTL keeps operator promo
// edits from being stale for long. (adv-review SF4)
const _dealCache = new Map();
const _DEAL_TTL_MS = 5 * 60 * 1000;

/* Care-guide excerpt (audit item 12, operator design): first real
   paragraph of the guide markdown — headings/tables/images skipped,
   inline markdown stripped — clamped to ~300 chars at a word boundary.
   Returns null when nothing usable remains. */
const CARE_EXCERPT_MAX_CHARS = 300;
const excerptFromMarkdown = (md) => {
  if (!md) return null;
  const para = md
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .find((p) => p && !p.startsWith('#') && !p.startsWith('|') && !p.startsWith('!['));
  if (!para) return null;
  const text = para
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')     // images out entirely
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')  // links → their text
    .replace(/[#*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;
  if (text.length <= CARE_EXCERPT_MAX_CHARS) return text;
  const cut = text.slice(0, CARE_EXCERPT_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return `${lastSpace > 0 ? cut.slice(0, lastSpace) : cut}…`;
};

const FrogProductDetailPage = () => {
  const { productId } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [product, setProduct] = useState(null);
  const [variations, setVariations] = useState([]);
  const [showMorphNames, setShowMorphNames] = useState(false);
  const [restockSubscriptions, setRestockSubscriptions] = useState({});
  // Product-level subscription (variation=NULL server-side): fires when ANY
  // variation of the product relists. Used by the sold-out empty state.
  const [productNotify, setProductNotify] = useState(false);
  // Bumped on every subscribe/unsubscribe: a hydration GET that was already
  // in flight when the user toggled must not overwrite the toggle's result
  // with its pre-toggle server snapshot (#128 r3).
  const notifyMutationRef = useRef(0);
  // Page-level "nothing available" notify affordance (customer slice 2026-08)
  const [emptyNotifyBusy, setEmptyNotifyBusy] = useState(false);
  // Unified-grid sort. Component state only — persists for as long as
  // the customer is on this page (incl. across cart adds, which don't
  // remount), resets on navigation. Deliberately NOT localStorage:
  // "Featured" is the merchandising default we want every arrival to
  // land on, and a sticky "Price: Low to High" would quietly bury
  // premium listings for that customer forever.
  const [sortMode, setSortMode] = useState(DEFAULT_SORT_MODE);
  // Cross-link (operator-found): the active promo landing page that
  // applies to this product/species, when one exists. {slug, title}.
  const [activeDeal, setActiveDeal] = useState(null);
  // Care-guide excerpt (audit item 12, operator design): short excerpt
  // string rendered between the buy-box and the listings grid when the
  // species has care-guide content; null otherwise.
  const [careExcerpt, setCareExcerpt] = useState(null);
  const [careGuideAvailable, setCareGuideAvailable] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await getFrogProductById(productId);
        setProduct(response);

        if (response.variations) {
          const sorted = [...response.variations].sort(
            (a, b) => b.is_for_sale - a.is_for_sale
          );
          setVariations(sorted);

          // Determine whether to show morphs
          const hasDistinctMorphs = sorted.some(
            (v) => {
              const name = v.morph?.name?.trim();
              return name && name.length > 0 && name.toLowerCase() !== 'normal';
            }
          );
          setShowMorphNames(hasDistinctMorphs);
        }
      } catch (error) {
        // Error handled by UI spinner
      }
    };

    fetchProduct();
  }, [productId]);

  // Check restock notification status (one call returns all variation subscriptions)
  useEffect(() => {
    if (!productId || !isAuthenticated) {
      setRestockSubscriptions({});
      setProductNotify(false);
      return undefined;
    }
    // Cancellation guard (#128 r2): on rapid product→product navigation a
    // slow response for the PREVIOUS product must not paint its
    // subscription state onto the current page. The mutation snapshot
    // (#128 r3) additionally drops the response if the user toggled a
    // subscription while this GET was in flight.
    let cancelled = false;
    const mutationSnapshot = notifyMutationRef.current;
    const stale = () => cancelled || notifyMutationRef.current !== mutationSnapshot;
    checkRestockNotifyAll(productId)
      .then(data => {
        if (stale()) return;
        setRestockSubscriptions(data.subscriptions || {});
        setProductNotify(!!data.product_level);
      })
      .catch(() => {
        if (stale()) return;
        setRestockSubscriptions({});
        setProductNotify(false);
      });
    return () => { cancelled = true; };
  }, [productId, isAuthenticated]);

  /* Care-guide excerpt (audit item 12): fetch the guide the same way
     SpeciesCareGuide does (public care-guide endpoint — always 200,
     empty strings when no content) and keep the first ~300 chars. The
     product payload carries no guide content, so this is the only way
     to know whether "Keeping <species>" has anything to say. One small
     cached GET (@cache_page(120) server-side), only when the product
     has a species. */
  useEffect(() => {
    const speciesId = product?.species?.id;
    if (!speciesId) {
      setCareExcerpt(null);
      return undefined;
    }
    // adv-review S3: clear immediately so product→product navigation
    // can't flash the PREVIOUS species' care text (and resurrect the
    // See-listings button) while the new fetch resolves.
    setCareExcerpt(null);
    setCareGuideAvailable(false);
    let cancelled = false;
    (async () => {
      try {
        const guide = await getPublicSpeciesCareGuide(speciesId);
        if (cancelled) return;
        const text = excerptFromMarkdown(
          guide?.overview_md || guide?.care_guide_md || ''
        );
        setCareExcerpt(text || null);
        // Same predicate SpeciesCareGuide's not-found state uses
        // (care_guide_available = bool(care_guide_md.strip()) server-side),
        // so the PDP never links to a guide page that will 404 (#130a).
        setCareGuideAvailable(!!(guide?.care_guide_md && guide.care_guide_md.trim()));
      } catch (e) {
        if (!cancelled) setCareExcerpt(null);
      }
    })();
    return () => { cancelled = true; };
  }, [product]);

  /* Cross-link (operator-found): when an active promotion applies to
     this product/species, link its landing page from the header. The
     detail payload's promo_display carries labels but NO slug, so the
     slug is resolved the same way the homepage cards get theirs — the
     public promos list (`store/promos/`) — then each promo page's
     featured_items (which DO carry product/species ids) is matched
     against this product. Only runs when promo_display says a promo
     applies, so non-promo products cost zero extra requests. */
  // adv-review SF4: the walk repeats on every product view and each
  // promo_detail response is a real DB hit server-side. Memoize the
  // resolved deal per product for 5 minutes, module-wide.
  useEffect(() => {
    const hasActivePromo = variations.some(
      (v) => v?.promo_display?.has_active_promo
    );
    if (!hasActivePromo || !product) {
      setActiveDeal(null);
      return undefined;
    }
    const cached = _dealCache.get(productId);
    if (cached && (Date.now() - cached.at) < _DEAL_TTL_MS) {
      setActiveDeal(cached.deal);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const promos = await getActivePromos();
        const speciesId = product?.species?.id;
        // Bounded walk: first matching promo wins (a handful of active
        // promos at most; each detail fetch is one public GET).
        const now = Date.now();
        for (const promo of (Array.isArray(promos) ? promos : []).slice(0, 8)) {
          if (!promo?.slug) continue;
          // adv-review SF2: store/promos/ filters is_active but NOT the
          // date window — without this, a scheduled September promo
          // could be advertised while August's deal sets the price.
          if (promo.start_date && Date.parse(promo.start_date) > now) continue;
          if (promo.end_date && Date.parse(promo.end_date) < now) continue;
          let detail;
          try {
            detail = await getPromoBySlug(promo.slug);
          } catch (e) {
            continue; // one broken promo page must not kill the walk
          }
          if (cancelled) return;
          const items = Array.isArray(detail?.featured_items)
            ? detail.featured_items : [];
          const applies = items.some((item) => (
            (item?.type === 'product' && item?.product?.id === productId)
            || (item?.type === 'species' && speciesId && item?.id === speciesId)
          ));
          if (applies) {
            const deal = {
              slug: promo.slug,
              title: detail.customer_facing_title || promo.name || 'Current promotion',
            };
            _dealCache.set(productId, { deal, at: Date.now() });
            setActiveDeal(deal);
            return;
          }
        }
        if (!cancelled) {
          _dealCache.set(productId, { deal: null, at: Date.now() });
          setActiveDeal(null);
        }
      } catch (e) {
        if (!cancelled) setActiveDeal(null);
      }
    })();
    return () => { cancelled = true; };
  }, [variations, product, productId]);

  const handleToggleRestockNotify = useCallback(async (variationId) => {
    const isSubscribed = !!restockSubscriptions[variationId];
    try {
      if (isSubscribed) {
        await unsubscribeRestockNotify(productId, variationId);
        // Bump only AFTER success: a failed mutation changes nothing
        // server-side, so an in-flight hydration GET is still the truth
        // and must be allowed to land (#128 r4).
        notifyMutationRef.current += 1;
        setRestockSubscriptions(prev => {
          const next = { ...prev };
          delete next[variationId];
          return next;
        });
      } else {
        await subscribeRestockNotify(productId, variationId);
        notifyMutationRef.current += 1;
        setRestockSubscriptions(prev => ({ ...prev, [variationId]: true }));
      }
    } catch (error) {
      console.warn('[RestockNotify] Toggle failed:', error?.response?.data?.error || error.message);
      throw error;
    }
  }, [productId, restockSubscriptions]);

  /* Flatten EVERYTHING purchasable on this product into one list.
     Deps (`variations`) are declared above this hook — project rule:
     the production minifier exposes TDZ that dev mode tolerates. */
  const listingItems = useMemo(() => {
    const items = [];
    variations.forEach((v) => {
      if (v.sales_mode === 'individual') {
        // I-02b — each eligible linked animal is its own listing.
        (Array.isArray(v.individual_animals) ? v.individual_animals : [])
          .forEach((animal) => {
            items.push({
              key: `animal-${animal.id}`,
              kind: 'animal',
              typeRank: FEATURED_TYPE_RANK.animal,
              price: toNumber(animal.price),
              createdAt: toTimestamp(animal.created_at),
              // API lists an animal only when it's purchasable.
              available: true,
              animal,
              variation: v,
            });
          });
      } else if (v.is_active !== false) {
        // Stock variation card (the classic count-based listing).
        // I-02 close-out N3: `is_active` (serializer: "has an active
        // price") false means the card can NEVER be bought — it rendered
        // as a dead "N/A · Sign Up" shell (or nothing) while still
        // counting toward "N available · M sold out". Inactive stock
        // variations are excluded from BOTH the count and the grid.
        // Strict `!== false` so payloads without the field change nothing.
        // A sold-out variation with a live price keeps its Notify card
        // (is_active stays true); its ADDITIVE lots below list either way.
        items.push({
          key: `stock-${v.id}`,
          kind: 'stock',
          typeRank: FEATURED_TYPE_RANK.stock,
          price: stockCardPrice(v),
          // ProductVariation carries NO created_at — stock cards have no
          // listing timestamp and sort last under "Recently added".
          createdAt: null,
          // Same test VariationCard uses to decide Add-to-Cart vs
          // Notify-Me. Sold-out stock cards sink in every sort mode.
          available: !!(v.is_for_sale && v.count > 0),
          variation: v,
        });
      }
      /* I-02d.3 ADDITIVE LOTS — `lots` is populated for EVERY variation
         regardless of sales mode (pinned pairs/groups AND pool bundles),
         listing alongside that variation's own stock/animal cards. */
      (Array.isArray(v.lots) ? v.lots : []).forEach((lot) => {
        items.push({
          key: `lot-${lot.id}`,
          kind: 'lot',
          typeRank: lot.lot_type === 'pair'
            ? FEATURED_TYPE_RANK.pair
            : FEATURED_TYPE_RANK.group,  // 'group' + 'pool_group'
          price: toNumber(lot.price),
          createdAt: toTimestamp(lot.created_at),
          // API lists a lot only when it's purchasable.
          available: true,
          lot,
          variation: v,
        });
      });
    });
    return items;
  }, [variations]);

  /* #102 deal-banner coherence (2026-08-17): the promo engine can only
     discount STOCK lines — individual-mode animals and pinned lots are
     engine-undiscountable by policy. A page whose purchasable listings
     are all animals/lots (stock cards absent or sold out) must not
     advertise a deal link that lands on honestly-undiscounted items.
     One available stock listing keeps the banner. Deps (`listingItems`)
     are declared above this hook — TDZ rule. */
  const hasDiscountableListing = useMemo(
    // adv-review: pool_group lots ARE engine-discountable too (pinned
    // pairs/individual animals are not) — a pool-bundle-only product
    // should still advertise its deal.
    () => listingItems.some((item) =>
      (item.kind === 'stock' && item.available)
      || (item.kind === 'lot' && item.lot?.lot_type === 'pool_group')),
    [listingItems]
  );

  /* Buy-box "From $X" (competitive benchmark 2026-08): the cheapest
     AVAILABLE listing, priced the way its card displays it (promo-aware
     via stockCardPrice inside listingItems). Derived from the same
     flattened list the grid renders, so the rail number always matches
     the cheapest card the "See listings" jump lands on. Deps
     (`listingItems`) are declared above this hook — TDZ rule. */
  const fromListingPrice = useMemo(() => {
    const prices = listingItems
      .filter((item) => item.available && item.price != null)
      .map((item) => item.price);
    return prices.length > 0 ? Math.min(...prices) : null;
  }, [listingItems]);

  /* Apply the chosen sort. Array.prototype.sort is stable (ES2019+), so
     every tie falls back to payload order — which keeps one variation's
     animals adjacent instead of shuffling them. */
  const sortedListingItems = useMemo(() => {
    const items = [...listingItems];
    if (sortMode === 'price_asc') {
      items.sort((a, b) => compareAvailability(a, b) || comparePrice(a, b, 1));
    } else if (sortMode === 'price_desc') {
      items.sort((a, b) => compareAvailability(a, b) || comparePrice(a, b, -1));
    } else if (sortMode === 'recent') {
      items.sort((a, b) => compareAvailability(a, b) || (() => {
        // Untimestamped items (stock cards, legacy animals with a null
        // created_at) sink to the bottom rather than claiming "newest".
        if (a.createdAt == null && b.createdAt == null) return 0;
        if (a.createdAt == null) return 1;
        if (b.createdAt == null) return -1;
        return b.createdAt - a.createdAt;  // newest first
      })());
    } else {
      // 'featured' (default): type order, then price DESC within type.
      items.sort((a, b) => compareAvailability(a, b)
        || (a.typeRank - b.typeRank)
        || comparePrice(a, b, -1));
    }
    return items;
  }, [listingItems, sortMode]);

  /* #98 Flagship pair slot (2026-08): the newest PAIR lot leaves the
     grid and renders as a full-width featured card above it. Pages with
     zero pair lots are byte-identical to today (degradation law #103).
     Group/pool lots never qualify. Deps (`listingItems`) declared
     above — TDZ rule. */
  const featuredPairItem = useMemo(() => {
    let best = null;
    listingItems.forEach((item) => {
      if (item.kind !== 'lot' || item.lot?.lot_type !== 'pair') return;
      /* Mirror LotListingCard's own render gates (price + at least one
         resolvable gallery image): a pair that would render null must
         not claim the featured slot — it stays in the grid where it
         renders nothing, exactly as today, instead of blanking the
         page's promoted listing. */
      if (item.price == null) return;
      const gallery = Array.isArray(item.lot.gallery) ? item.lot.gallery : [];
      if (!gallery.some((g) => g && (g.thumb_url || g.url))) return;
      /* Newest wins the slot; an untimestamped lot never outranks a
         timestamped one. Ties fall back to payload order (first kept). */
      if (!best) {
        best = item;
      } else if (item.createdAt != null
        && (best.createdAt == null || item.createdAt > best.createdAt)) {
        best = item;
      }
    });
    return best;
  }, [listingItems]);

  /* The grid renders everything EXCEPT the featured pair. Counts stay
     on sortedListingItems — which still contains the featured item — so
     "N available" keeps counting it (count honesty, audit item 11). */
  const gridListingItems = useMemo(() => (
    featuredPairItem
      ? sortedListingItems.filter((item) => item.key !== featuredPairItem.key)
      : sortedListingItems
  ), [sortedListingItems, featuredPairItem]);

  /* #103 — every listing on the page exists but none is purchasable
     (all sold-out stock cards; lots/animals are only listed when
     purchasable). The anonymous arrivals capture renders under the
     listings toolbar. Derived, not a hook — `sortedListingItems` is
     declared above. */
  const allListingsSoldOut = sortedListingItems.length > 0
    && !sortedListingItems.some((item) => item.available);

  const getCommonName = () => productPageTitle(product);

  // True only when the species' published guide has body content — set by
  // the excerpt fetch above with the same predicate the guide page's
  // not-found state uses. (Replaces a stub that returned true for every
  // species and linked Auratus to a guide that doesn't exist — #130a.)
  const hasCareGuideContent = () => careGuideAvailable;

  // Resolve hero image for display (still used for SEO og:image)
  const sImg = product ? resolveImageUrls(product.species) : { url: null };
  const heroSrc = sImg.url ?? null;
  const heroAlt = formatSpeciesName(product?.species, { withLocality: false });
  // Photo Cascade S2-01.1 — full gallery for the carousel (cover + species
  // gallery from the API `gallery` field; falls back to the single hero).
  const galleryImages = product ? resolveGalleryImages(product) : [];

  // Generate SEO metadata
  const commonName = getCommonName();
  // The species' scientific name in proper form (operator ruling
  // 2026-10-10) — the product NAME is the operator's own text and is
  // only the fallback when no species is attached.
  const scientificName = (product?.species?.genus && product?.species?.species)
    ? formatSpeciesName(product.species, { withLocality: false })
    : formatProductName(product);
  const genus = product?.species?.genus || product?.genus || '';

  // Cowork beta run (2026-09-05): a plant (and a package) was breadcrumbed
  // "Home > Frogs > ...". The DETAIL ROUTE is genuinely generic —
  // /frogs/products/<id> is the only product-detail route in App.js, and
  // every card links there regardless of type — so the fix is the crumb
  // LABEL, not the URL. Category pages (/plants, /geckos, /frogs) do
  // exist, so the second crumb points at the right one.
  // Genus pages exist for frogs and geckos ONLY (same rule ProductCard
  // applies to its "More from <genus>" link), so a plant gets no genus
  // crumb rather than a link into the frogs URL space.
  // SPECIES ROW FIRST, product column as fallback — the precedence every
  // backend check uses (serializers, signals, the prune command). The
  // product column is a non-null int defaulting to 0 (FROG), so reading
  // it first makes the `??` fallback dead code and crumbs an unstamped
  // plant as "Frogs" (adv-review 2026-09-05).
  const productSpeciesType =
    product?.species?.species_type ?? product?.species_type ?? 0;
  const CATEGORY_CRUMBS = {
    0: { name: 'Frogs', url: '/frogs', hasGenusPages: true },
    1: { name: 'Plants', url: '/plants', hasGenusPages: false },
    // AQUATIC (2) was deprecated 2026-05-30 when the facility pivoted to
    // geckos and has no browse page — so the crumb is a LABEL with no
    // link rather than one pointing at /frogs, which it isn't.
    2: { name: 'Aquatics', url: null, hasGenusPages: false },
    3: { name: 'Geckos', url: '/geckos', hasGenusPages: true },
    // I-03 close 1c: "Other reptile" has NO shop page (operator
    // 2026-10-24) — a label with no link, never filed under Frogs.
    4: { name: 'Reptiles', url: null, hasGenusPages: false },
  };
  const categoryCrumb = CATEGORY_CRUMBS[productSpeciesType] || CATEGORY_CRUMBS[0];
  const locality = product?.locality || '';
  const fullImageUrl = heroSrc ? buildAssetUrl(heroSrc) : 'https://feelingfroggy.llc/logos/ff_original_96px.png';

  // Get first available variation for price and availability
  const firstVariation = variations.find(v => v.is_for_sale) || variations[0];

  // GSC Fix: Compute real product price from variations
  // Priority 1: Use first available variation's price
  // Priority 2: Compute minimum price across all for-sale variations
  // Priority 3: Fall back to first variation's price
  const getProductPrice = () => {
    // Try first available variation
    const firstAvailPrice = firstVariation?.latest_price?.price;
    if (firstAvailPrice) {
      return parseFloat(firstAvailPrice).toFixed(2);
    }

    // Compute min price from all active/for-sale variations
    const activePrices = variations
      .filter(v => v.is_for_sale && v.latest_price?.price)
      .map(v => parseFloat(v.latest_price.price));

    if (activePrices.length > 0) {
      return Math.min(...activePrices).toFixed(2);
    }

    // Fallback to first variation's price or 0.00
    const fallbackPrice = variations[0]?.latest_price?.price;
    return fallbackPrice ? parseFloat(fallbackPrice).toFixed(2) : '0.00';
  };

  const price = getProductPrice();
  const isAvailable = variations.some(v => v.is_for_sale && v.count > 0);

  // Build enhanced product name with taxonomy details for SEO/JSON-LD
  const buildEnhancedProductName = () => {
    const isDartFrog = genus && ['dendrobates', 'ranitomeya', 'oophaga', 'phyllobates'].includes(genus.toLowerCase());
    const isRedEyedTreeFrog = genus?.toLowerCase() === 'agalychnis' && product?.species?.species?.toLowerCase() === 'callidryas';

    // Dart frogs: append locality if available
    if (isDartFrog && locality) {
      return `${commonName} ${formatLocality(locality, product?.species?.species_type)}`;
    }

    // Red-eyed tree frogs: append morph (default to "Normal" if missing)
    if (isRedEyedTreeFrog) {
      const morph = firstVariation?.morph?.name || 'Normal';
      return `${commonName} (${morph})`;
    }

    // Default: just common name
    return commonName;
  };

  const enhancedProductName = buildEnhancedProductName();

  // Generate Product JSON-LD
  // Use enhanced product name for better SEO visibility
  const productSchema = product ? createProductSchema({
    productId,
    name: enhancedProductName,  // Enhanced name with locality/morph
    commonName: enhancedProductName,
    scientificName,
    locality,
    imageUrl: fullImageUrl,
    price,
    isAvailable,
    genus,
    speciesType: product?.species?.species_type,
    isLiveAnimal: true  // Omit offers for live animals
  }) : null;

  // Generate Breadcrumb JSON-LD
  const breadcrumbSchema = product ? createBreadcrumbSchema([
    { name: 'Home', url: 'https://feelingfroggy.llc/' },
    ...(categoryCrumb.url
      ? [{ name: categoryCrumb.name,
           url: `https://feelingfroggy.llc${categoryCrumb.url}` }]
      : []),
    ...(categoryCrumb.hasGenusPages && genus
      ? [{
          name: `${genus.charAt(0).toUpperCase() + genus.slice(1)}`,
          url: `https://feelingfroggy.llc${categoryCrumb.url}/${genus.toLowerCase()}`,
        }]
      : []),
    { name: enhancedProductName, url: `https://feelingfroggy.llc/frogs/products/${productId}` }
  ]) : null;

  // Get genus-specific keywords
  const keywords = genus ? getGenusKeywords(genus) : [];

  // SEO title and description
  const seoTitle = `${enhancedProductName} for Sale`;
  const seoDescription = locality
    ? `${scientificName} ${formatLocality(locality, product?.species?.species_type)} - Captive-bred ${commonName.toLowerCase()} with live arrival guarantee. Shop now at Feeling Froggy.`
    : `${scientificName} - Captive-bred ${commonName.toLowerCase()} with live arrival guarantee. Shop now at Feeling Froggy.`;

  return (
    <>
      {product && (
        <SEO
          title={seoTitle}
          description={seoDescription}
          canonical={`https://feelingfroggy.llc/frogs/products/${productId}`}
          keywords={keywords}
          jsonLd={[productSchema, breadcrumbSchema]}
          ogImage={fullImageUrl}
        />
      )}
      <Container className="products-page">
        {product ? (
        <>
          <Breadcrumbs items={[
            { name: "Home", url: "/" },
            ...(categoryCrumb.url
              ? [{ name: categoryCrumb.name, url: categoryCrumb.url }]
              : []),
            ...(categoryCrumb.hasGenusPages && genus
              ? [{ name: genus, url: `${categoryCrumb.url}/${genus.toLowerCase()}` }]
              : []),
            { name: formatProductName(product) || commonName, url: `/frogs/products/${productId}` }
          ]} />

          {/* mb-3 (was mb-4) — pulls the listing toolbar up toward the
              product header so it no longer floats in whitespace. */}
          {/* PDP buy box (competitive benchmark 2026-08): at >=992px this
              header becomes a two-column grid — hero carousel LEFT, sticky
              info rail RIGHT — so price + a path to the buy controls sit
              above the fold (they didn't at 1920x934). Below 992px the
              grid never engages and everything stacks exactly as before
              (rail text centers via .frog-detail-rail). Same elements,
              relocated — nothing rebuilt. */}
          <div className="frog-detail-header frog-detail-buybox mb-3">
            <div className="frog-detail-hero">
              <div className="frog-detail-image-wrapper text-center">
                {/* Photo Cascade S2-01.1 — multi-photo carousel (species
                    gallery). Per-slide license attribution renders inside the
                    carousel; single-image products render a plain <img>. */}
                <PhotoCarousel
                  images={galleryImages}
                  alt={heroAlt || getCommonName()}
                />
              </div>
            </div>
            <div className="frog-detail-rail">
              {/* 2026-07-20 SEO: was <h2>, but this IS the page's primary
                  heading (the money page). Promoted to a single semantic
                  <h1> — one h1 per page for on-page SEO signal.
                  #90c: .h2 sizing replaced by .ff-heading-h1 — the ONE
                  customer page-title treatment. (text-center utility moved
                  to .frog-detail-rail CSS so desktop can left-align.) */}
              <h1 className="ff-heading-h1">{getCommonName()}</h1>
              {/* The species subtitle is species vocabulary. A box has
                  none: it says what it IS instead, and never the anchor's
                  locality. */}
              {product.is_package ? (
                product.package_summary ? (
                  <p className="text-muted" data-testid="package-summary">
                    {product.package_summary}
                  </p>
                ) : null
              ) : (
                <p className="text-muted">
                  <em>{scientificName}</em>
                  {product.locality && <>&nbsp;{formatLocality(product.locality, product.species?.species_type)}</>}
                </p>
              )}
              {/* A package's care guide would be its ANCHOR species' — a
                  species the shopper was never promised. */}
              {!product.is_package && hasCareGuideContent() && product?.species && (
                <p className="mb-0">
                  <Link to={`/care-guides/${createSpeciesSlug(product.species)}`} className="care-guide-link">
                    <BookOpen size={14} className="ff-link-icon" aria-hidden="true" focusable="false" />
                    Care Guide
                  </Link>
                </p>
              )}
              {/* Cross-link (operator-found): the promo landing page for an
                  active deal on this product/species belongs right here by
                  the species name, beside the Care Guide link.
                  #102: suppressed when nothing on the page is engine-
                  discountable — see hasDiscountableListing above. */}
              {activeDeal && hasDiscountableListing && (
                <p className="mb-0 mt-1">
                  <Link to={`/promo/${activeDeal.slug}`} className="promo-deal-link">
                    <Tag size={14} className="ff-link-icon" aria-hidden="true" focusable="false" />
                    {activeDeal.title} — View deal
                  </Link>
                </p>
              )}
              {/* Desktop-only rail CTA: "From $X" + a jump to the listing
                  grid. The real buy controls live on the per-variation
                  cards below — a rail CTA that scrolls to them is the
                  honest v1 (no duplicate Add-to-Cart wiring). d-lg-*
                  matches the 992px grid breakpoint, so mobile flow is
                  untouched. */}
              {sortedListingItems.length > 0 && (
                <div className="frog-detail-rail-cta d-none d-lg-block">
                  {fromListingPrice != null && (
                    <p className="frog-detail-from-price">
                      From ${fromListingPrice.toFixed(2)}
                    </p>
                  )}
                  {/* Cowork audit 2026-08-14 (item 12): measured pointless
                      on 15/15 pages — the grid started 211px below the
                      button, already visible. The jump only earns its
                      place when the care-guide excerpt section renders
                      between the buy-box and the grid and actually pushes
                      the listings down; otherwise no button. */}
                  {careExcerpt && (
                    <button
                      type="button"
                      className="ff-btn ff-btn--secondary frog-detail-see-listings"
                      onClick={() => {
                        // Below the sticky navbar at every width (R5 B1
                        // review: the 120px clearance is short of the
                        // 159px bar at phone width).
                        scrollIntoViewBelowTopBar(
                          document.getElementById('product-listings'));
                      }}
                    >
                      See listings
                    </button>
                  )}
                </div>
              )}
              {/* Trust block (competitive benchmark 2026-08): in the rail
                  on desktop; on mobile the rail stacks under the hero, so
                  the same instance lands right after the title block. */}
              <TrustHighlights />
            </div>
          </div>

          {/* Care-guide excerpt (audit item 12, operator design): a short
              "Keeping <species>" section between the buy-box and the
              listings grid — the guide's first paragraph plus a link to
              the full care guide (same route the rail link uses). Only
              when the species actually has guide content. */}
          {careExcerpt && product?.species && (
            <section className="care-excerpt mb-4" aria-labelledby="care-excerpt-title">
              <h2 className="care-excerpt-title" id="care-excerpt-title">
                Keeping {getCommonName()}
              </h2>
              <p className="care-excerpt-text">{careExcerpt}</p>
              {/* Excerpt can exist on overview_md alone; the guide PAGE
                  requires care_guide_md — so the link needs its own gate
                  or it dead-ends like the Auratus one did (#130a r2). */}
              {careGuideAvailable && (
                <Link
                  to={`/care-guides/${createSpeciesSlug(product.species)}`}
                  className="care-guide-link care-excerpt-link"
                >
                  Read the full care guide →
                </Link>
              )}
            </section>
          )}

          {(() => {
            /* ONE unified, sortable grid of everything purchasable on
               this product — pinned pairs, pinned groups, pool bundles,
               individual animals and stock variation cards, side by
               side. The cards themselves are untouched (each already
               badges its own type), so this is layout + ORDER only:
               no change to what's purchasable, pricing, or cart wiring.
               Ordering comes from `sortedListingItems` above. */
            return (
              <>
                {sortedListingItems.length > 0 && (
                  <>
                    {/* Listing toolbar (2026-08-07): the sort control used
                        to float alone, right-aligned, on its own line with a
                        wide empty gutter — it read as a stray dropdown. It
                        is now the right half of a real toolbar whose left
                        half carries the listing count, tucked directly above
                        the grid and up against the product header. */}
                    {/* id = target of the rail's "See listings" jump;
                        scroll-margin-top in products.css clears the
                        sticky navbar. Renders under the same
                        `length > 0` gate as the rail CTA, so the jump
                        target always exists when the button does. */}
                    <div className="listing-toolbar" id="product-listings">
                      <span className="listing-toolbar-count">
                        {(() => {
                          /* Count honesty (Cowork audit 2026-08-14, item
                             11): "5 listings" over a grid with 1 buyable
                             card counted sold-out stock as listings. Say
                             what a shopper can actually buy: "N available"
                             when everything is purchasable, "X available ·
                             Y sold out" when mixed. `available` is the
                             same per-item flag every sort mode uses. */
                          const avail = sortedListingItems
                            .filter((item) => item.available).length;
                          const soldOut = sortedListingItems.length - avail;
                          return soldOut === 0
                            ? `${avail} available`
                            : `${avail} available · ${soldOut} sold out`;
                        })()}
                      </span>
                      {/* Control hides when the GRID has a single listing —
                          nothing to reorder, and it would just crowd one
                          card. The featured pair (#98) sits outside the
                          sortable grid, so it doesn't count here. */}
                      {gridListingItems.length > 1 && (
                        <div className="listing-sort-bar">
                          <label className="listing-sort-label" htmlFor="listing-sort">
                            Sort by
                          </label>
                          <select
                            id="listing-sort"
                            className="listing-sort-select"
                            value={sortMode}
                            onChange={(e) => setSortMode(e.target.value)}
                          >
                            {SORT_MODES.map((m) => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          {/* Screen-reader confirmation that the reorder
                              happened — a silently re-ordered grid is
                              invisible to a non-sighted customer. */}
                          <span className="visually-hidden" aria-live="polite">
                            {/* #98: announce the GRID count — the featured
                                pair above it never reorders. */}
                            {`Showing ${gridListingItems.length} listings sorted by `
                              + `${(SORT_MODES.find((m) => m.value === sortMode) || {}).label}`}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* #103 — everything listed is sold out: anonymous
                        arrivals capture (per-species) under the toolbar. */}
                    {allListingsSoldOut && (
                      <ArrivalNotifyForm
                        compact
                        speciesId={product?.species?.id || null}
                        prompt={`Everything here is sold out — get an email when new ${getCommonName()} animals arrive`}
                        className="mb-4"
                      />
                    )}
                    {/* #98 flagship pair slot — full-width featured card
                        ABOVE the grid, below the toolbar so the
                        "N available" count (which includes it) covers the
                        whole listings section. Renders ONLY when a
                        qualifying pair lot exists — zero-pairs pages
                        unchanged. */}
                    {featuredPairItem && (
                      <LotListingCard
                        key={featuredPairItem.key}
                        featured
                        lot={featuredPairItem.lot}
                        variation={featuredPairItem.variation}
                        variationLabel={variationLabel(featuredPairItem.variation)}
                        product={product}
                      />
                    )}
                    {/* listings-grid (Cowork re-measure 2026-08-14): scopes
                        the equal-height column stretch in products.css —
                        cols become flex columns so every card fills its
                        row's height and mixed-type rows (657px lot card
                        beside 586px stock cards) bottom out level. */}
                    {gridListingItems.length > 0 && (
                    <Row className="g-4 listings-grid">
                      {gridListingItems.map((item) => (
                        <Col key={item.key} xs={12} sm={6} md={4}>
                          {item.kind === 'stock' ? (
                            <VariationCard
                              variation={item.variation}
                              product={product}
                              showMorphNames={showMorphNames}
                              restockSubscribed={!!restockSubscriptions[item.variation.id]}
                              onToggleRestockNotify={() => handleToggleRestockNotify(item.variation.id)}
                            />
                          ) : item.kind === 'animal' ? (
                            <AnimalListingCard
                              animal={item.animal}
                              variation={item.variation}
                              variationLabel={variationLabel(item.variation)}
                              product={product}
                            />
                          ) : (
                            <LotListingCard
                              lot={item.lot}
                              variation={item.variation}
                              variationLabel={variationLabel(item.variation)}
                              product={product}
                            />
                          )}
                        </Col>
                      ))}
                    </Row>
                    )}
                  </>
                )}
                {sortedListingItems.length === 0 && (() => {
                  /* Notify-me empty state (customer slice 2026-08, reworked
                     punch #128a): PRODUCT-LEVEL subscribe (no variation_id →
                     variation=NULL record server-side), so the customer is
                     emailed when ANY variation of this product relists — not
                     just whichever variation happened to be picked here.
                     Pre-#128 empty-state subscribers hold VARIATION-level
                     records — count those as "on" too, and the variationless
                     DELETE clears every pending record for the product, so
                     this single toggle never lies in either direction. */
                  const subscribed = productNotify
                    || Object.keys(restockSubscriptions).length > 0;
                  const handleEmptyNotify = async () => {
                    setEmptyNotifyBusy(true);
                    try {
                      if (subscribed) {
                        await unsubscribeRestockNotify(productId);
                        notifyMutationRef.current += 1;
                        setProductNotify(false);
                        setRestockSubscriptions({});
                      } else {
                        await subscribeRestockNotify(productId);
                        notifyMutationRef.current += 1;
                        setProductNotify(true);
                      }
                    } catch (error) {
                      console.warn('[RestockNotify] Product-level toggle failed:',
                        error?.response?.data?.error || error.message);
                    } finally {
                      setEmptyNotifyBusy(false);
                    }
                  };
                  return (
                    <div className="text-center py-4">
                      <p className="text-muted mb-3">
                        No animals currently available — get notified when
                        new ones are listed.
                      </p>
                      {isAuthenticated ? (
                        <>
                          <button
                            type="button"
                            className="ff-btn ff-btn--secondary"
                            onClick={handleEmptyNotify}
                            disabled={emptyNotifyBusy}
                          >
                            {emptyNotifyBusy
                              ? 'Updating...'
                              : subscribed
                                ? 'Notifications On 🔔'
                                : 'Notify Me When Available'}
                          </button>
                          {subscribed && user?.email && (
                            <p className="text-muted small mt-2 mb-0">
                              We&apos;ll email {user.email} when animals are listed.
                            </p>
                          )}
                        </>
                      ) : (
                        <Link to="/signup" className="ff-btn ff-btn--secondary">
                          Sign Up to Get Notified
                        </Link>
                      )}
                    </div>
                  );
                })()}
              </>
            );
          })()}

          {/* Weather-safety trust block (customer slice 2026-08) */}
          <WeatherTrustBlock />

          {/* Footer Care Guide Link */}
          {hasCareGuideContent() && product?.species && (
            <div className="text-center mt-5 pt-4 border-top">
              <Link to={`/care-guides/${createSpeciesSlug(product.species)}`} className="ff-btn ff-btn--secondary">
                <BookOpen size={16} className="ff-link-icon" aria-hidden="true" focusable="false" />
                View Complete Care Guide
              </Link>
            </div>
          )}
        </>
        ) : (
          /* Loading skeleton: hero block + card grid shimmer */
          <div className="mt-4" role="status" aria-label="Loading product">
            <div className="ff-skeleton ff-skeleton-hero" />
            <div className="ff-skeleton ff-skeleton-hero-line" />
            <div className="ff-skeleton ff-skeleton-hero-line" style={{ maxWidth: 220 }} />
            <div className="mt-4">
              <SkeletonCardGrid count={3} />
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

/**
 * The page's H1.
 *
 * S3-01.6 (Cowork re-check #1) — a PACKAGE goes by its OWN name. The
 * server blanks a package's `common_names` (they are the ANCHOR
 * species'), so this fell through to "genus species" and the page titled
 * a box of cuttings "QADELETE testplant" while the breadcrumb right
 * above it said the box's name.
 */
export const productPageTitle = (product) => {
  if (product?.is_package) return formatProductName(product) || 'Plant package';
  if (product?.common_names?.length) return product.common_names[0];
  // Fallback to scientific name instead of "Unnamed Frog"
  if (product?.species?.genus && product?.species?.species) {
    return formatSpeciesName(product.species, { withLocality: false });
  }
  if (product?.name) return formatProductName(product);
  return 'Species Name Unavailable';
};

export default FrogProductDetailPage;